import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Search, PlusSquare, LayoutDashboard, Shield, LogOut, Building2,
  ClipboardList, ClipboardCheck, Heart, User, X, Settings, ListChecks,
  LogIn, UserPlus, ChevronRight, HeartHandshake,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getFavoriteIds } from "@/lib/favorites";

// Desktop nav items — unchanged behaviour from before.
const desktopItems = (isAdmin) => [
  { to: "/", label: "Search", testid: "nav-search" },
  { to: "/post-requirement", label: "Post Your Requirements", testid: "nav-post-requirement" },
  { to: "/add/property", label: "Post Property", testid: "nav-post-property" },
  { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" },
  { to: "/assistance", label: "Assistance", testid: "nav-assistance" },
  ...(isAdmin ? [{ to: "/admin", label: "Admin", testid: "nav-admin" }] : []),
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const items = desktopItems(isAdmin);

  const [postSheet, setPostSheet] = useState(false);
  const [accountSheet, setAccountSheet] = useState(false);

  // Close any open sheet on route change.
  useEffect(() => { setPostSheet(false); setAccountSheet(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-xl mpn-gradient-hero grid place-items-center text-white shadow-sm">
              <Building2 className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-[15px] sm:text-base">AcresDeal</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground -mt-0.5">Buy · Sell · Rent · Lease</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid + "-desktop"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition ${
                    isActive ? "bg-accent text-accent-foreground" : "text-foreground/70 hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <div className="hidden sm:flex flex-col items-end leading-tight">
                  <span className="text-sm font-semibold">{user.name}</span>
                  <span className="text-[11px] text-muted-foreground capitalize">
                    {user.role === "admin" ? "admin" : "member"}
                  </span>
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); navigate("/login"); }}
                  className="p-2 rounded-lg hover:bg-muted"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <Link data-testid="login-link" to="/login" className="mpn-btn-ghost h-9 px-3 text-sm">Login</Link>
                <Link data-testid="register-link" to="/register" className="mpn-btn-primary h-9 px-3 text-sm">Register</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main key={location.pathname} className="max-w-6xl mx-auto px-4 pt-4 pb-28 md:pb-10 page-enter">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <MobileBottomNav
        pathname={location.pathname}
        onOpenPost={() => setPostSheet(true)}
        onOpenAccount={() => setAccountSheet(true)}
        postOpen={postSheet}
        accountOpen={accountSheet}
      />

      {/* Post sheet */}
      <BottomSheet open={postSheet} onClose={() => setPostSheet(false)} testid="post-sheet" title="What would you like to post?">
        <SheetLink to="/add/property" icon={<Building2 className="w-5 h-5" />}
                   title="Post Property" desc="List a property for Sell, Rent or Lease"
                   testid="sheet-post-property" />
        <SheetLink to="/post-requirement" icon={<ClipboardList className="w-5 h-5" />}
                   title="Post Requirement" desc="Tell us what you're looking for — we'll match you"
                   testid="sheet-post-requirement" />
      </BottomSheet>

      {/* Account sheet */}
      <BottomSheet open={accountSheet} onClose={() => setAccountSheet(false)} testid="account-sheet"
                   title={user ? user.name : "Welcome"}
                   subtitle={user ? (user.role === "admin" ? "Administrator" : "Member") : "Sign in to manage listings and requirements"}>
        {user ? (
          <>
            <SheetLink to="/dashboard" icon={<LayoutDashboard className="w-5 h-5" />}
                       title="Dashboard" desc="Overview of your listings and activity"
                       testid="sheet-dashboard" />
            <SheetLink to="/dashboard#properties" icon={<Building2 className="w-5 h-5" />}
                       title="My Listings" desc="Manage your posted properties"
                       testid="sheet-my-listings" />
            <SheetLink to="/dashboard#requirements" icon={<ListChecks className="w-5 h-5" />}
                       title="My Requirements" desc="Requirements you've raised"
                       testid="sheet-my-requirements" />
            <SheetLink to="/dashboard#profile" icon={<User className="w-5 h-5" />}
                       title="Profile" desc="Name, contact and public profile"
                       testid="sheet-profile" />
            <SheetLink to="/dashboard#settings" icon={<Settings className="w-5 h-5" />}
                       title="Settings" desc="Notifications and preferences"
                       testid="sheet-settings" />
            <SheetLink to="/assistance" icon={<HeartHandshake className="w-5 h-5" />}
                       title="Assistance" desc="Talk to our team — call, WhatsApp or post a requirement"
                       testid="sheet-assistance" />
            {isAdmin ? (
              <SheetLink to="/admin" icon={<Shield className="w-5 h-5" />}
                         title="Admin Console" desc="Platform control room"
                         testid="sheet-admin" />
            ) : null}
            <button
              type="button"
              onClick={() => { setAccountSheet(false); logout(); navigate("/login"); }}
              data-testid="sheet-logout"
              className="w-full flex items-center gap-3 rounded-2xl border border-rose-100 bg-rose-50 hover:bg-rose-100 p-3.5 text-left transition"
            >
              <span className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-600 grid place-items-center shrink-0">
                <LogOut className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm text-rose-700">Log out</span>
                <span className="block text-xs text-rose-600/80">Sign out of this device</span>
              </span>
            </button>
          </>
        ) : (
          <>
            <SheetLink to="/login" icon={<LogIn className="w-5 h-5" />}
                       title="Log in" desc="Access your dashboard and listings"
                       testid="sheet-login" accent />
            <SheetLink to="/register" icon={<UserPlus className="w-5 h-5" />}
                       title="Create account" desc="Join AcresDeal — free forever"
                       testid="sheet-register" />
          </>
        )}
      </BottomSheet>
    </div>
  );
}

// -------------------- Mobile bottom nav --------------------

function MobileBottomNav({ pathname, onOpenPost, onOpenAccount, postOpen, accountOpen }) {
  const [savedCount, setSavedCount] = useState(() => getFavoriteIds().length);
  useEffect(() => {
    const sync = () => setSavedCount(getFavoriteIds().length);
    window.addEventListener("favorites:changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("favorites:changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const isSearch = pathname === "/";
  const isReqs = pathname.startsWith("/requirements") || pathname.startsWith("/post-requirement");
  const isSaved = pathname.startsWith("/saved");
  const isAccount = accountOpen || pathname.startsWith("/dashboard") || pathname.startsWith("/admin")
                    || pathname.startsWith("/login") || pathname.startsWith("/register");

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-white/95 backdrop-blur-xl"
      data-testid="mobile-bottom-nav"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="grid grid-cols-5 items-end">
        <TabLink to="/" active={isSearch} icon={<Search className="w-5 h-5" />} label="Search" testid="mnav-search" />
        <TabLink to="/post-requirement" active={isReqs} icon={<ClipboardCheck className="w-5 h-5" />} label="Requirements" testid="mnav-requirements" />
        <TabButton
          testid="mnav-post"
          active={postOpen}
          onClick={onOpenPost}
          aria-label="Post"
        >
          <span className="w-14 h-14 -mt-7 rounded-full mpn-gradient-hero grid place-items-center text-white shadow-lg ring-4 ring-white">
            <PlusSquare className="w-6 h-6" />
          </span>
          <span className="mt-1 text-[11px] font-semibold text-primary">Post</span>
        </TabButton>
        <TabLink to="/saved" active={isSaved} icon={
          <span className="relative">
            <Heart className="w-5 h-5" />
            {savedCount > 0 ? (
              <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold grid place-items-center">
                {savedCount > 9 ? "9+" : savedCount}
              </span>
            ) : null}
          </span>
        } label="Saved" testid="mnav-saved" />
        <TabButton
          testid="mnav-account"
          active={isAccount}
          onClick={onOpenAccount}
          aria-label="Account"
        >
          <User className="w-5 h-5" />
          <span className="mt-0.5 text-[11px] font-medium">Account</span>
        </TabButton>
      </div>
    </nav>
  );
}

function TabLink({ to, icon, label, active, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={`flex flex-col items-center justify-center py-2.5 text-[11px] font-medium min-h-[58px] ${active ? "text-primary" : "text-muted-foreground"}`}
    >
      {icon}
      <span className="mt-0.5">{label}</span>
    </NavLink>
  );
}

function TabButton({ children, onClick, active, testid, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      {...rest}
      className={`flex flex-col items-center justify-center py-2.5 min-h-[58px] ${active ? "text-primary" : "text-muted-foreground"}`}
    >
      {children}
    </button>
  );
}

// -------------------- Bottom sheet --------------------

function BottomSheet({ open, onClose, title, subtitle, testid, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="md:hidden fixed inset-0 z-40" data-testid={testid}>
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in"
        onClick={onClose}
        data-testid={`${testid}-backdrop`}
      />
      <div
        className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl border-t border-border shadow-2xl p-4 pb-8"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}
      >
        <div className="mx-auto w-10 h-1.5 rounded-full bg-muted-foreground/25 mb-3" />
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight truncate">{title}</div>
            {subtitle ? <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testid}-close`}
            className="w-9 h-9 rounded-full hover:bg-muted grid place-items-center shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-2 max-h-[70vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

function SheetLink({ to, icon, title, desc, testid, accent }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-3 rounded-2xl border p-3.5 text-left transition ${accent ? "border-primary/30 bg-teal-50/60 hover:bg-teal-50" : "border-border bg-white hover:bg-muted/40"}`}
    >
      <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${accent ? "mpn-gradient-hero text-white" : "bg-teal-50 text-primary"}`}>
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-sm">{title}</span>
        <span className="block text-xs text-muted-foreground">{desc}</span>
      </span>
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </Link>
  );
}
